/**
 * 2048 PK 横屏竞技场 DOM 视图骨架结构 (Horizontal Widescreen Arena HTML Template)
 * 包含：
 * 1. 竞速 / 无限 双模式巨型主入口 (#modal-lobby)
 * 2. 红方x3 · 蓝方x3 · 观战xN 战术席位大厅与实时聊天室 (#modal-seat-lobby)
 * 3. 左友方&聊天 · 中主战盘 · 右敌方x3 雷达的纯正横屏竞技场 (.pk-battlefield)
 * 4. 终局盘面透视复盘与双阵营对抗战报弹窗 (#modal-gameover)
 */

export const getArenaHtml = (): string => {
  return `
  <div class="pk-arena-root">
    <!-- 顶部横屏状态栏 HUD -->
    <header class="pk-hud-header">
      <div class="hud-brand">
        <div class="hud-title-group">
          <h1 class="hud-title">2048 PK</h1>
          <span class="hud-mode-pill" id="hud-mode-pill">2048 竞速</span>
        </div>
      </div>

      <div class="hud-room-badge" id="hud-room-badge">
        <span class="room-label">ROOM</span>
        <span class="room-code" id="hud-room-code">------</span>
        <button class="btn-copy-code" id="btn-copy-link" title="复制房间邀请链接">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
      </div>

      <!-- 顶栏中央：红蓝双阵营实时比分对抗条 + 标准模式倒计时 / 战报播报 / 60s 追分决胜倒计时 -->
      <div class="hud-team-tug-wrap">
        <div class="hud-team-scores">
          <span class="hud-team-red">红方 <b id="hud-red-score">0</b></span>
          <div class="hud-center-status">
            <span class="hud-match-timer" id="hud-match-timer" style="display: none;">⏱ 08:00</span>
            <div class="pk-combat-log" id="pk-combat-log">
              <span class="log-item">对战已就绪</span>
            </div>
            <!-- 显眼且不遮挡棋盘的 60s 追分决胜倒计时胶囊（激活时接管顶栏中央位） -->
            <div class="pk-chase-banner" id="pk-chase-banner" style="display: none;">
              <span class="chase-text" id="pk-chase-text">⏱ 60s · 追分决胜</span>
            </div>
          </div>
          <span class="hud-team-blue"><b id="hud-blue-score">0</b> 蓝方</span>
        </div>
        <div class="hud-tug-bar">
          <div class="hud-tug-red-fill" id="hud-team-tug-red" style="width: 50%;"></div>
        </div>
      </div>

      <div class="hud-net-badge" id="hud-net-badge">
        <span class="net-dot"></span>
        <span class="net-text" id="hud-net-text">就绪</span>
      </div>

      <div class="hud-actions">
        <button class="hud-btn" id="btn-sound-toggle" title="音效开关">
          <svg class="icon-sound-on" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
          </svg>
          <svg class="icon-sound-off" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" style="display: none;">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
            <line x1="23" y1="9" x2="17" y2="15"></line>
            <line x1="17" y1="9" x2="23" y2="15"></line>
          </svg>
        </button>
        <button class="hud-btn" id="btn-dpad-toggle" title="虚拟方向键">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="2" y="6" width="20" height="12" rx="4"></rect>
            <line x1="6" y1="12" x2="10" y2="12"></line>
            <line x1="8" y1="10" x2="8" y2="14"></line>
            <line x1="15" y1="13" x2="15.01" y2="13"></line>
            <line x1="18" y1="11" x2="18.01" y2="11"></line>
          </svg>
        </button>
        <button class="hud-btn hud-btn-danger" id="btn-exit-game" title="返回席位 / 退出">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16 17 21 12 16 7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
        </button>
      </div>
    </header>

    <!-- 横屏三栏主战场区域：左(友方/战况 + 聊天室) · 中(主战棋盘) · 右(对手棋盘) -->
    <main class="pk-battlefield">
      <!-- 左栏：友方阵营/战况 + 局内聊天室 -->
      <aside class="battle-col ally-chat-zone">
        <div class="sub-panel allies-panel">
          <div class="sub-panel-header">
            <span class="sub-panel-title">实时战况</span>
            <span class="sub-panel-hint" id="ally-rescue-hint">对战中</span>
          </div>
          <div class="allies-mini-grid" id="allies-mini-container"></div>
        </div>

        <div class="sub-panel battle-chat-panel">
          <div class="battle-chat-messages" id="battle-chat-list"></div>
          <div class="battle-chat-bottom-hud">
            <div class="emote-bar" id="emote-bar">
              <button class="btn-emote" data-emoji="GG" type="button">GG</button>
              <button class="btn-emote" data-emoji="WP" type="button">WP</button>
              <button class="btn-emote" data-emoji="GL" type="button">GL</button>
              <button class="btn-emote" data-emoji="!!" type="button">!!</button>
            </div>
            <div class="battle-chat-input-row">
              <input type="text" class="form-input battle-chat-input" id="battle-chat-input" maxlength="80" placeholder="发送消息..." />
              <button class="btn-chat-send" id="btn-battle-chat-send" type="button">发送</button>
            </div>
          </div>
        </div>
      </aside>

      <!-- 中栏：主战 4x4 棋盘区 -->
      <section class="battle-col player-zone local-zone" id="local-player-zone">
        <div class="zone-header">
          <div class="player-profile">
            <span class="player-avatar-tag" id="local-team-tag">RED</span>
            <div class="profile-info">
              <span class="player-name" id="local-player-name">数字猎手</span>
              <span class="player-role-badge" id="local-role-badge">HOST</span>
            </div>
          </div>

          <!-- DMC 华丽度评级仪表 -->
          <div class="dmc-widget" id="local-dmc-widget">
            <div class="dmc-letter-wrap">
              <span class="dmc-letter" id="local-dmc-letter">D</span>
            </div>
            <div class="dmc-gauge-wrap">
              <div class="dmc-gauge-bar" id="local-dmc-gauge-bar" style="width: 0%;"></div>
            </div>
          </div>

          <!-- 计分看板 -->
          <div class="score-card">
            <span class="score-title">SCORE</span>
            <span class="score-number" id="local-score">0</span>
            <div class="score-popup" id="local-score-popup"></div>
          </div>
        </div>

        <!-- 攻击蓄能状态条 -->
        <div class="attack-charge-wrap">
          <div class="charge-label-row">
            <span class="charge-title" id="local-rescue-status">攻击蓄能</span>
            <span class="charge-status" id="local-attack-status">压制态</span>
          </div>
          <div class="charge-track">
            <div class="charge-fill" id="local-charge-fill" style="width: 0%;"></div>
          </div>
        </div>

        <!-- 4x4 本地互动棋盘 -->
        <div class="board-wrapper" id="local-board-wrapper" data-theme="sandstone">
          <div class="grid-container" id="local-grid-container">
            ${Array.from({ length: 16 })
              .map((_, i) => `<div class="grid-cell" data-idx="${i}"></div>`)
              .join("")}
          </div>
          <div class="tile-container" id="local-tile-container"></div>
          <div class="danger-vignette" id="local-danger-vignette"></div>
          <div class="fog-blind-layer" id="local-fog-layer"></div>
        </div>

        <!-- 虚拟十字键 (可按需展开) -->
        <div class="virtual-dpad" id="virtual-dpad" style="display: none;">
          <button class="dpad-btn dpad-up" data-dir="UP">▲</button>
          <button class="dpad-btn dpad-left" data-dir="LEFT">◀</button>
          <button class="dpad-btn dpad-right" data-dir="RIGHT">▶</button>
          <button class="dpad-btn dpad-down" data-dir="DOWN">▼</button>
        </div>
      </section>

      <!-- 右栏：敌方阵营棋盘区 -->
      <section class="battle-col player-zone rival-zone" id="rival-player-zone">
        <div class="sub-panel-header rival-radar-header">
          <div class="player-profile">
            <span class="player-avatar-tag rival" id="rival-team-tag">BLUE</span>
            <div class="profile-info">
              <span class="player-name" id="rival-player-name">对手</span>
              <span class="player-role-badge rival-badge" id="rival-role-badge">对手</span>
            </div>
          </div>

          <div class="dmc-widget rival-dmc" id="rival-dmc-widget">
            <div class="dmc-letter-wrap">
              <span class="dmc-letter" id="rival-dmc-letter">D</span>
            </div>
            <div class="dmc-gauge-wrap">
              <div class="dmc-gauge-bar" id="rival-dmc-gauge-bar" style="width: 0%;"></div>
            </div>
          </div>

          <div class="score-card rival-score-card">
            <span class="score-title">SCORE</span>
            <span class="score-number" id="rival-score">0</span>
          </div>
        </div>

        <!-- 敌方多盘矩阵容器 -->
        <div class="enemies-mini-grid single-enemy" id="enemies-mini-container">
          <div class="enemy-board-card" id="primary-enemy-card">
            <div class="enemy-card-top">
              <span class="enemy-card-name" id="primary-enemy-name">对手</span>
              <span class="enemy-card-lock" data-role="enemy-score">0分</span>
            </div>
            <div class="rival-mirror-wrapper" id="rival-mirror-wrapper">
              <div class="rival-grid-container">
                ${Array.from({ length: 16 })
                  .map(() => `<div class="rival-grid-cell"></div>`)
                  .join("")}
              </div>
              <div class="rival-tile-container" id="rival-tile-container"></div>
              <div class="rival-danger-vignette" id="rival-danger-vignette"></div>
            </div>
          </div>
          <div id="extra-enemies-slot" class="extra-enemies-slot"></div>
        </div>

        <div class="rival-status-bar">
          <span class="rival-status-text" id="rival-status-text">最高合成: 2</span>
        </div>
      </section>
    </main>

    <!-- 全屏弹射与礼花动效图层 (z-index: 260) -->
    <div class="pk-effect-overlay" id="pk-effect-overlay"></div>

    <!-- 全局操作过渡遮罩 -->
    <div class="pk-loading-overlay" id="pk-loading-overlay" style="display: none;">
      <div class="loading-spinner"></div>
      <span class="loading-text" id="loading-status-text">正在处理...</span>
    </div>

    <!-- ==========================================================================
         阶段 1：极简横屏首页 (#modal-lobby) —— 半透明毛玻璃双主入口
         ========================================================================== -->
    <div class="pk-modal-backdrop" id="modal-lobby">
      <div class="lobby-minimal-shell">
        <!-- 顶栏：仅保留品牌、昵称与房号输入 -->
        <div class="lobby-minimal-topbar">
          <span class="brand-title">2048 PK</span>

          <div class="lobby-top-controls">
            <div class="nickname-field">
              <span class="nickname-tag">昵称</span>
              <input type="text" class="form-input nickname-input" id="input-player-name" maxlength="12" value="数字猎手" placeholder="昵称" />
            </div>

            <button class="btn-sub-action btn-toggle-join" id="btn-toggle-join-drawer" type="button">
              <span>房号加入</span>
            </button>
          </div>
        </div>

        <!-- 折叠式房号输入栏 -->
        <div class="join-drawer-row" id="join-drawer-row" style="display: none;">
          <input type="text" class="form-input join-code-input" id="input-room-code" placeholder="输入 6 位房间号" maxlength="6" />
          <button class="btn-drawer-join" id="btn-join-room" type="button">
            <span id="btn-join-text">进入</span>
          </button>
          <button class="btn-drawer-cancel" id="btn-cancel-join" type="button" title="关闭">✕</button>
        </div>

        <!-- 三个半透明大字主入口：极速模式 / 标准模式 / 无限模式 -->
        <div class="mode-portals-grid">
          <button class="mode-portal-card portal-speed" id="btn-portal-speed" type="button">
            <span class="portal-big-title">极速模式</span>
            <span class="portal-short-sub">3分钟决战 · 率先合出 2048 获胜</span>
          </button>

          <button class="mode-portal-card portal-2048" id="btn-portal-2048" type="button">
            <span class="portal-big-title" id="btn-create-text">标准模式</span>
            <span class="portal-short-sub">8分钟决战 · 率先合出 2048 获胜</span>
          </button>

          <button class="mode-portal-card portal-endless" id="btn-portal-endless" type="button">
            <span class="portal-big-title">无限模式</span>
            <span class="portal-short-sub">无上限 · 决战至封盘</span>
          </button>
        </div>

        <!-- 底部简洁在线房间栏 -->
        <div class="rooms-minimal-bar">
          <div class="rooms-section-header">
            <div class="rooms-title-group">
              <span class="rooms-title">公开房间</span>
              <span class="rooms-count-pill" id="rooms-count-badge">0</span>
            </div>
            <button class="btn-sub-action" id="btn-refresh-rooms" type="button">刷新</button>
          </div>
          <div class="rooms-card-grid" id="lobby-rooms-list"></div>
        </div>
      </div>
    </div>

    <!-- ==========================================================================
         阶段 2：横屏席位页 (#modal-seat-lobby) —— 红方x3 · 聊天室 · 蓝方x3 · 观战xN
         ========================================================================== -->
    <div class="pk-modal-backdrop" id="modal-seat-lobby" style="display: none;">
      <div class="pk-modal-card seat-lobby-card">
        <div class="seat-lobby-header">
          <div class="seat-header-left">
            <button class="btn-seat-back" id="btn-seat-back" type="button">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4"><polyline points="15 18 9 12 15 6"></polyline></svg>
              <span>返回</span>
            </button>
            <div class="seat-room-badge">
              <span class="seat-room-label">房号</span>
              <span class="seat-room-code" id="seat-room-code">------</span>
              <button class="btn-seat-copy" id="btn-seat-copy-link" type="button">复制链接</button>
            </div>
            <span class="seat-format-pill" id="seat-format-pill">1v1</span>
          </div>

          <div class="seat-mode-switcher">
            <button class="seat-mode-btn" id="tab-mode-speed" data-mode="speed_2048" type="button">极速 3:00</button>
            <button class="seat-mode-btn active" id="tab-mode-2048" data-mode="classic_2048" type="button">标准 8:00</button>
            <button class="seat-mode-btn" id="tab-mode-endless" data-mode="endless" type="button">无限</button>
          </div>
        </div>

        <!-- 三栏主体：红方 x3 | 聊天室 | 蓝方 x3 -->
        <div class="seat-lobby-body">
          <div class="team-seats-col team-col-red">
            <div class="team-col-header">
              <span class="team-col-dot red-dot"></span>
              <span class="team-col-title">红方</span>
              <span class="team-col-count" id="red-team-count">0/3</span>
            </div>
            <div class="seats-stack" id="red-seats-container"></div>
          </div>

          <div class="seat-center-col">
            <div class="seat-chat-box">
              <div class="seat-chat-header">
                <span>聊天室</span>
              </div>
              <div class="seat-chat-messages" id="seat-chat-list"></div>
              <div class="seat-chat-input-row">
                <input type="text" class="form-input seat-chat-input" id="seat-chat-input" maxlength="80" placeholder="发送消息..." />
                <button class="btn-chat-send" id="btn-seat-chat-send" type="button">发送</button>
              </div>
            </div>

            <div class="seat-launch-bar">
              <div class="countdown-banner" id="seat-countdown-banner" style="display: none;">
                <span class="countdown-number" id="seat-countdown-num">3</span>
                <span class="countdown-text" id="seat-countdown-text">秒后开局</span>
              </div>

              <button class="btn-launch-match" id="btn-seat-start" type="button">
                <span id="btn-seat-start-text">开始对决</span>
              </button>
              <button class="btn-cancel-countdown" id="btn-seat-cancel" type="button" style="display: none;">
                <span>取消开始</span>
              </button>
            </div>
          </div>

          <div class="team-seats-col team-col-blue">
            <div class="team-col-header">
              <span class="team-col-dot blue-dot"></span>
              <span class="team-col-title">蓝方</span>
              <span class="team-col-count" id="blue-team-count">0/3</span>
            </div>
            <div class="seats-stack" id="blue-seats-container"></div>
          </div>
        </div>

        <!-- 底栏：观战席 -->
        <div class="seat-spectator-footer">
          <div class="spectator-left">
            <span class="spectator-title">观战席</span>
            <button class="btn-spectator-join" id="btn-join-spectator" type="button" title="进入观战">＋ 加入观战</button>
          </div>
          <div class="spectator-list" id="seat-spectator-list"></div>
        </div>

        <button id="btn-create-room" style="display: none;"></button>
        <button id="btn-practice-ai" style="display: none;"></button>
      </div>
    </div>

    <!-- ==========================================================================
         阶段 4：半透明毛玻璃分栏结算中心 (#modal-gameover)
         左半屏：选中选手终局 4x4 棋盘快照与个人高光徽章
         右半屏：红蓝双阵营五列战报榜（逐行错峰滑入 + MVP/SVP + 五维专项金章）
         支持一键「透视全场」切换查看完整 6 盘战场与聊天室
         ========================================================================== -->
    <div class="pk-modal-backdrop pk-settlement-overlay" id="modal-gameover" style="display: none;">
      <!-- 透视全场模式下的顶部悬浮迷你控制条 -->
      <div class="settlement-inspect-floating-bar" id="settlement-inspect-bar">
        <span class="inspect-bar-tip">👁️ 正在透视全场终局盘面（可自由发送聊天消息）</span>
        <div class="inspect-bar-actions">
          <button class="btn-inspect-restore" id="btn-restore-report" type="button">📊 返回战报榜</button>
          <button class="btn-dock-primary" id="btn-inspect-rematch" type="button">⚔️ 再来一局</button>
        </div>
      </div>

      <!-- 主结算毛玻璃大卡 -->
      <div class="settlement-modal-card" id="gameover-card">
        <!-- 1. 顶栏：VICTORY/DEFEAT 巨幅徽章 + 决胜原因 + 红蓝阵营比分拉锯对比 -->
        <header class="settlement-header-bar">
          <div class="settlement-verdict-left">
            <span class="gameover-banner" id="gameover-banner">VICTORY</span>
            <div class="settlement-verdict-meta">
              <div class="settlement-verdict-top">
                <span class="gameover-mode-tag" id="gameover-mode-tag">竞速模式</span>
                <span class="gameover-reason" id="gameover-reason">已决出胜负</span>
              </div>
              <div class="vs-tug-track">
                <div class="vs-tug-fill-my" id="gameover-vs-fill-my" style="width: 50%;"></div>
              </div>
            </div>
          </div>

          <div class="settlement-vs-pill">
            <span class="vs-team-label red">红方</span>
            <span class="vs-side-score my-score" id="stat-my-score">0</span>
            <span class="dock-vs-colon">:</span>
            <span class="vs-side-score rival-score" id="stat-rival-score">0</span>
            <span class="vs-team-label blue">蓝方</span>
            <span class="vs-diff-pill" id="gameover-score-diff">平局</span>
          </div>
        </header>

        <!-- 2. 主体分栏：左(纯粹 4x4 终局棋盘快照) + 右(无边框红蓝全员单行战报榜) -->
        <div class="settlement-split-grid">
          <!-- 左栏：选中选手终局 4x4 棋盘快照（无重复小卡片、无外框） -->
          <section class="settlement-board-showcase">
            <div class="showcase-player-header">
              <div class="showcase-player-left">
                <span class="showcase-team-dot" id="settlement-focus-team-dot"></span>
                <span class="showcase-player-name" id="settlement-focus-name">数字猎手</span>
                <div class="showcase-badges-row" id="settlement-focus-badges"></div>
              </div>
              <div class="showcase-rating-box">
                <span class="showcase-rating-num" id="settlement-focus-rating">12.0</span>
                <span class="rank-prestige-badge rank-d" id="stat-my-rank">D</span>
              </div>
            </div>

            <div class="showcase-board-stage">
              <div class="settlement-snapshot-wrapper" id="settlement-snapshot-board">
                <div class="snapshot-grid-unified" id="settlement-snapshot-tiles"></div>
              </div>
            </div>

            <div class="showcase-board-caption">
              点击右侧任意选手切换查看终局盘面
            </div>

            <!-- 保留隐藏兼容节点 -->
            <div style="display: none;">
              <span id="settlement-focus-score">0</span>
              <span id="stat-my-max">2</span>
              <span id="stat-slash-hits">0</span>
              <span id="stat-score-drained">0</span>
              <span id="stat-shield-blocks">0</span>
            </div>
          </section>

          <!-- 右栏：无边框红蓝全员单行五列战报榜（3v3 全员 6 人一屏完整同框） -->
          <section class="settlement-scoreboard-pane">
            <div class="scoreboard-col-headers">
              <span class="col-h-player">选手 & 荣誉称号</span>
              <span class="col-h-tile">最大块</span>
              <span class="col-h-score">得分</span>
              <span class="col-h-combat">攻防贡献</span>
              <span class="col-h-rating">评分</span>
            </div>

            <div class="scoreboard-teams-scroll" id="settlement-scoreboard-body"></div>
          </section>
        </div>

        <!-- 3. 底栏：MVP 荣耀播报 + 操作按钮 -->
        <footer class="settlement-footer-bar">
          <div class="settlement-mvp-callout" id="settlement-mvp-callout">
            🏆 战报统计已完成
          </div>

          <div class="dock-actions-group">
            <button class="btn-dock-secondary" id="btn-inspect-board" type="button">👁️ 透视全场</button>
            <button class="btn-dock-primary" id="btn-rematch" type="button">⚔️ 再来一局</button>
            <button class="btn-dock-secondary" id="btn-back-lobby" type="button">退出房间</button>
          </div>
        </footer>

        <span id="stat-rival-max" style="display: none;">2</span>
      </div>
    </div>
  </div>
  `;
};
